"use client";

import { Shield, Sparkles, Scale, Droplets, Sun, Brain, type LucideIcon } from "lucide-react";
import { useTranslations } from "next-intl";

import { type GoalStack, type PeptideGoal } from "@/config/peptide-stacks";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Icon map
// ---------------------------------------------------------------------------

const GOAL_ICONS: Record<PeptideGoal, LucideIcon> = {
	recovery: Shield,
	"anti-aging": Sparkles,
	"weight-management": Scale,
	skin: Droplets,
	tanning: Sun,
	cognitive: Brain,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GoalSelectorProps {
	goalStacks: GoalStack[];
	activeGoal: string | null;
	onSelectGoal: (goal: string | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GoalSelector({ goalStacks, activeGoal, onSelectGoal }: GoalSelectorProps) {
	const t = useTranslations("stackBuilder");

	function handleClick(goal: PeptideGoal) {
		// Toggle off if already selected
		onSelectGoal(activeGoal === goal ? null : goal);
	}

	return (
		<div>
			<p className="mb-3 text-sm font-medium text-muted-foreground">{t("goalQuestion")}</p>

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
				{goalStacks.map((stack) => {
					const Icon = GOAL_ICONS[stack.goal] ?? Shield;
					const isActive = activeGoal === stack.goal;

					return (
						<button
							key={stack.goal}
							type="button"
							onClick={() => handleClick(stack.goal)}
							className={cn(
								"flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-3 text-center transition-all",
								"hover:border-accent hover:shadow-sm",
								isActive ? "bg-accent/10 border-accent shadow-sm" : "border-border bg-card",
							)}
							aria-pressed={isActive}
						>
							<Icon className={cn("h-6 w-6 shrink-0", isActive ? "text-accent" : "text-muted-foreground")} />
							<span
								className={cn(
									"text-xs font-semibold leading-tight",
									isActive ? "text-foreground" : "text-muted-foreground",
								)}
							>
								{t(`goals.${stack.nameKey}`)}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}
